import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
export const Route = createFileRoute("/user/_layout/crm/contacts")({
  component: ContactsPage,
});

import { useState, useEffect, useMemo } from "react";
import instance from "@/lib/axios";
import { getCrmUrl } from "@/lib/crm";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ContactTable } from "@/components/crm/tables/ContactTable";
import { ContactFormSheet } from "@/components/crm/forms/ContactFormSheet";
import {
  useFetchContacts,
  useContactMutations,
  type ContactOrdering,
} from "@/hooks/api/crm/useContacts";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import type { CrmContact, CrmContactPayload } from "@/types/crm";

function ContactsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        first_name: SortDirection;
        title: SortDirection;
        email: SortDirection;
        assigned_to__first_name: SortDirection;
      },
      ContactOrdering
    >({
      first_name: null,
      title: null,
      email: null,
      assigned_to__first_name: null,
    });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  // Undefined = all team-visible records; pick a user to narrow.
  const [assignedTo, setAssignedTo] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);

  const location = useLocation();
  const viewId = new URLSearchParams(location.searchStr).get("view") ?? undefined;
  const navigate = useNavigate();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<CrmContact | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<CrmContact | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const contactsQuery = useFetchContacts({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    assigned_to: assignedTo ? String(assignedTo) : undefined,
    ordering: getApiOrdering(),
  });
  const contacts = contactsQuery.data?.results ?? [];
  const count = contactsQuery.data?.count ?? 0;

  const { createContact, updateContact, deleteContact } =
    useContactMutations();
  const isMutating =
    createContact.isPending || updateContact.isPending || deleteContact.isPending;
  const isLoading = contactsQuery.isPending || isMutating;
  const isFetching = contactsQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((count ?? 0) / pageSize)),
    [count, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, assignedTo, ordering, setPage]);

  useEffect(() => {
    if (!viewId) return;
    instance
      .get<CrmContact>(getCrmUrl(`contacts/${viewId}/`))
      .then((res) => {
        setEditingContact(res.data);
        setIsViewOnly(true);
        setIsSheetOpen(true);
      })
      .catch(() =>
        navigate({ to: "/user/crm/contacts", search: {} })
      );
  }, [viewId]);

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    if (viewId)
      navigate({ to: "/user/crm/contacts", search: {} });
  };

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setAssignedTo(undefined);
    setOrdering({
      first_name: null,
      title: null,
      email: null,
      assigned_to__first_name: null,
    });
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const onCreate = async (data: CrmContactPayload) => {
    await createContact.mutateAsync(data);
    toast.success("Contact created successfully!");
    setIsSheetOpen(false);
  };

  const onEdit = async (id: string, data: Partial<CrmContactPayload>) => {
    await updateContact.mutateAsync({ id, payload: data });
    toast.success("Contact updated successfully!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;
    try {
      await deleteContact.mutateAsync({ id: contactToDelete.id });
      toast.success("Contact deleted successfully!");
      setIsDeleteDialogOpen(false);
      setContactToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Contacts"
          description="Manage your CRM contacts."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => {
            setEditingContact(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Contact"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search contacts..."
          />
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} />
        </FiltersPanel>

        <ContactTable
          contacts={contacts}
          onView={(c) => {
            navigate({
              to: "/user/crm/contacts/$id",
              params: { id: String(c.id) },
            });
          }}
          onViewAccount={(accountId) => {
            navigate({
              to: "/user/crm/accounts/$id",
              params: { id: accountId },
            });
          }}
          onEdit={(c) => {
            setEditingContact(c);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(c) => {
            setContactToDelete(c);
            setIsDeleteDialogOpen(true);
          }}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <ContactFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={handleCloseSheet}
          onCreate={onCreate}
          onEdit={onEdit}
          editingContact={editingContact}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Contact"
          description={`Are you sure you want to delete "${contactToDelete?.first_name} ${contactToDelete?.last_name}"?`}
          itemName={`${contactToDelete?.first_name} ${contactToDelete?.last_name}`}
          isLoading={deleteContact.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
